// AI-Powered Recommendation Generator
// Uses OpenRouter API to generate personalized recommendations

import { generateRecommendationsWithFallback } from '../services/openrouter/client';
import recommendationMenu from '../data/recommendationMenu.json';
import { generateRecommendations as generateStaticRecommendations } from './assessmentScoring';

/**
 * Build structured prompt for AI recommendation generation
 * @param {Object} assessmentData - Full assessment data including scores and form responses
 * @param {Object} recommendationMenu - Menu of available recommendations
 * @returns {Object} - {systemPrompt, userPrompt}
 */
function buildPrompt(assessmentData, recommendationMenu) {
  const { scores, formData, currentStage, targetStage } = assessmentData;
  
  // Extract key context from form data
  const goals = formData.goals_and_priorities || {};
  const challenges = goals.biggest_concerns || [];
  const priorityUseCases = goals.priority_use_cases || '';
  const successDefinition = goals.success_definition || '';
  
  // Build system prompt
  const systemPrompt = `You are an expert AI strategy consultant analyzing an organization's AI readiness assessment.
Your task is to select the 2-3 most impactful next steps from a curated recommendation menu.

Consider:
- Overall maturity score and current/target stage
- Weakest dimensions that need immediate attention
- Specific question responses showing gaps
- Organization's stated goals and challenges

Return ONLY valid JSON matching this exact schema:
{
  "recommendations": [
    {
      "id": "menu-item-id",
      "rationale": "brief explanation (1-2 sentences) of why this is the most impactful next step for this organization"
    }
  ]
}

IMPORTANT:
- Select ONLY 2-3 recommendations
- Use ONLY IDs from the provided menu
- Prioritize High priority items for low-scoring dimensions
- Consider suitableForStages when selecting
- Provide specific, actionable rationale based on their assessment data`;

  // Build user prompt with assessment data
  const userPrompt = `## Assessment Summary

**Overall Score:** ${scores.overall.toFixed(2)} / 5.0
**Current Maturity Stage:** ${currentStage || 'Not specified'}
**Target Stage:** ${targetStage || 'Not specified'}

**Dimension Scores:**
- Strategy: ${scores.strategy.toFixed(2)} / 5.0
- Data: ${scores.data.toFixed(2)} / 5.0  
- Technology: ${scores.technology.toFixed(2)} / 5.0
- People: ${scores.people.toFixed(2)} / 5.0
- Governance: ${scores.governance.toFixed(2)} / 5.0

**Weakest Dimensions:** ${getWeakestDimensions(scores).join(', ')}

**Organization's Goals:**
${priorityUseCases || 'Not specified'}

**Success Definition:**
${successDefinition || 'Not specified'}

**Biggest Challenges:**
${challenges.length > 0 ? challenges.join(', ') : 'Not specified'}

## Available Recommendations Menu

${JSON.stringify(recommendationMenu.recommendations, null, 2)}

## Instructions

Based on the assessment data above, select 2-3 most impactful recommendations from the menu.
Focus on:
1. Addressing the weakest dimensions first
2. Aligning with their stated goals and challenges
3. Considering their current maturity stage
4. Providing quick wins vs. long-term transformation based on their readiness

Return your selection as valid JSON with the schema provided in the system prompt.`;

  return { systemPrompt, userPrompt };
}

/**
 * Get the two weakest dimensions from scores
 * @param {Object} scores - Dimension scores
 * @returns {Array<string>} - Array of dimension names
 */
function getWeakestDimensions(scores) {
  return Object.entries(scores)
    .filter(([key]) => key !== 'overall')
    .sort((a, b) => a[1] - b[1])
    .slice(0, 2)
    .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));
}

/**
 * Validate and map AI response to full recommendation objects
 * @param {Object} aiResponse - Response from AI with recommendation IDs
 * @param {Object} menu - Recommendation menu
 * @returns {Array} - Array of full recommendation objects
 */
function validateAndMapRecommendations(aiResponse, menu) {
  if (!aiResponse || !aiResponse.recommendations || !Array.isArray(aiResponse.recommendations)) {
    console.error('Invalid AI response format:', aiResponse);
    return null;
  }

  const recommendations = aiResponse.recommendations
    .map(({ id, rationale }) => {
      const menuItem = menu.recommendations.find(item => item.id === id);
      
      if (!menuItem) {
        console.warn(`AI selected invalid recommendation ID: ${id}`);
        return null;
      }

      return {
        dimension: menuItem.dimension,
        priority: menuItem.priority,
        action: menuItem.action,
        impact: menuItem.impact,
        rationale: rationale || '', // AI's specific rationale for this organization
      };
    })
    .filter(Boolean); // Remove any null entries

  if (recommendations.length === 0) {
    console.error('No valid recommendations after mapping');
    return null;
  }

  return recommendations;
}

/**
 * Generate AI-powered recommendations for an assessment
 * @param {Object} assessmentData - Assessment data including scores and form data
 * @param {Object} assessmentData.scores - Calculated dimension scores
 * @param {Object} assessmentData.formData - Raw form data
 * @param {string} assessmentData.currentStage - Current maturity stage
 * @param {string} assessmentData.targetStage - Target maturity stage
 * @returns {Promise<Array>} - Array of recommendations
 */
export async function generateAIRecommendations(assessmentData) {
  console.log('=== GENERATING AI RECOMMENDATIONS ===');
  console.log('Assessment data:', {
    overallScore: assessmentData.scores.overall,
    dimensions: Object.keys(assessmentData.scores).filter(k => k !== 'overall'),
  });

  try {
    // Build prompts
    const { systemPrompt, userPrompt } = buildPrompt(assessmentData, recommendationMenu);
    
    console.log('Prompt built. User prompt length:', userPrompt.length, 'characters');
    
    // Call OpenRouter API with fallback
    const aiResponse = await generateRecommendationsWithFallback({
      systemPrompt,
      userPrompt,
    });

    // If all AI attempts failed, fall back to static recommendations
    if (!aiResponse) {
      console.log('AI generation failed. Falling back to static recommendations.');
      return generateStaticRecommendations(
        assessmentData.scores,
        assessmentData.currentStage,
        assessmentData.targetStage
      ).recommendations;
    }

    // Validate and map AI response to full recommendation objects
    const mappedRecommendations = validateAndMapRecommendations(aiResponse, recommendationMenu);

    if (!mappedRecommendations) {
      console.log('AI response validation failed. Falling back to static recommendations.');
      return generateStaticRecommendations(
        assessmentData.scores,
        assessmentData.currentStage,
        assessmentData.targetStage
      ).recommendations;
    }

    console.log('=== AI RECOMMENDATIONS SUCCESS ===');
    console.log('Generated', mappedRecommendations.length, 'recommendations');
    
    return mappedRecommendations;
  } catch (error) {
    console.error('Error generating AI recommendations:', error);
    
    // Fall back to static recommendations
    console.log('Falling back to static recommendations due to error.');
    return generateStaticRecommendations(
      assessmentData.scores,
      assessmentData.currentStage,
      assessmentData.targetStage
    ).recommendations;
  }
}

